import { z } from 'zod';

/**
 * What an agent's workspace offers them (owner, 3 October 2026: "enable and disable features like
 * auto answer, conference, mute, allow hang up etc from the tms and it should update on the
 * workspace ... dispo and notes and tags ... at a queue level with an agent config override").
 *
 * The shape wrap-up already has (0111): the ACCOUNT's default, a QUEUE's own setting, and the
 * AGENT's override, most specific first. Each level holds only what it sets; a key it does not hold
 * is inherited. Where an agent takes calls for two queues that disagree, OFF wins: the stricter,
 * as 0111 decided for wrap-up.
 *
 * docs/36 §8.4 sketched named "control profiles"; this is the simpler form the owner asked for.
 * Mute, hold, hang up, keypad and auto-answer are the browser's own actions, so switching them off
 * hides them (docs/36 §8.4: closing the tab still ends a call).
 */
export const WORKSPACE_CONTROLS = [
  { key: 'auto_answer', label: 'Auto-answer', group: 'Calls', default: false,
    hint: 'Calls are answered by themselves after a short tone, without pressing Answer.' },
  { key: 'mute', label: 'Mute', group: 'Calls', default: true, hint: 'The agent can mute their microphone on a call.' },
  { key: 'hold', label: 'Hold', group: 'Calls', default: true, hint: 'The agent can put a caller on hold.' },
  { key: 'hang_up', label: 'Hang up', group: 'Calls', default: true,
    hint: 'The agent can end a call from the workspace. Off, the caller ends it (closing the tab still does).' },
  { key: 'keypad', label: 'Keypad', group: 'Calls', default: true, hint: 'The agent can send tones on a call, for menus.' },
  { key: 'transfer', label: 'Transfer', group: 'Transfers', default: true, hint: 'The agent can hand a call straight to somebody else.' },
  { key: 'talk_first', label: 'Talk-first transfer', group: 'Transfers', default: true,
    hint: 'The agent can put the caller on hold and talk to the colleague before handing over.' },
  { key: 'conference', label: 'Three-way calls', group: 'Transfers', default: true,
    hint: 'The agent can join the caller and a colleague on one call.' },
  { key: 'manual_dial', label: 'Dial out', group: 'Dialling', default: true,
    hint: 'The agent can make calls from the dial pad, colleagues and recent calls.' },
  { key: 'outcome', label: 'Outcome', group: 'After the call', default: true, hint: 'The agent marks what the call was.' },
  { key: 'notes', label: 'Notes', group: 'After the call', default: true, hint: 'The agent adds notes to a call.' },
  { key: 'tags', label: 'Tags', group: 'After the call', default: true, hint: 'The agent tags a call.' },
] as const;

export type WorkspaceControlKey = (typeof WORKSPACE_CONTROLS)[number]['key'];
export const WORKSPACE_CONTROL_KEYS = WORKSPACE_CONTROLS.map((c) => c.key) as unknown as readonly [WorkspaceControlKey, ...WorkspaceControlKey[]];
export type WorkspaceControls = Record<WorkspaceControlKey, boolean>;
/** What one level sets: only the keys it holds. */
export type WorkspaceControlsSet = { [K in WorkspaceControlKey]?: boolean | undefined };
export type ControlSource = 'agent' | 'queue' | 'account' | 'default';

/** A level's settings as saved: known keys, true or false. Anything else is refused. */
export const WorkspaceControlsSetSchema = z.object(
  Object.fromEntries(WORKSPACE_CONTROLS.map((c) => [c.key, z.boolean().optional()])) as Record<WorkspaceControlKey, z.ZodOptional<z.ZodBoolean>>,
).strict();

/**
 * The controls an agent has, and where each came from. Pure: the agent's own setting, then their
 * queues' (off wins between queues), then the account's, then the built-in default.
 */
export function resolveWorkspaceControls(
  account: WorkspaceControlsSet | null | undefined,
  queues: ReadonlyArray<WorkspaceControlsSet | null | undefined>,
  agent: WorkspaceControlsSet | null | undefined,
): { controls: WorkspaceControls; from: Record<WorkspaceControlKey, ControlSource> } {
  const controls = {} as WorkspaceControls;
  const from = {} as Record<WorkspaceControlKey, ControlSource>;
  for (const c of WORKSPACE_CONTROLS) {
    const k = c.key;
    const own = agent?.[k];
    const set = queues.map((q) => q?.[k]).filter((v): v is boolean => typeof v === 'boolean');
    if (typeof own === 'boolean') { controls[k] = own; from[k] = 'agent'; }
    else if (set.length) { controls[k] = set.every(Boolean); from[k] = 'queue'; }
    else if (typeof account?.[k] === 'boolean') { controls[k] = account[k]!; from[k] = 'account'; }
    else { controls[k] = c.default; from[k] = 'default'; }
  }
  return { controls, from };
}
